<template>
	<div class="ai_md">
		<template v-for="(block, index) in nodes">
			<p v-if="block.type === 'paragraph'" :key="`p${index}`" class="ai_md_p"
				><AiMarkdownInline :nodes="block.children"
			/></p>

			<component
				v-else-if="block.type === 'heading'"
				:is="headingTag(block.level)"
				:key="`h${index}`"
				class="ai_md_heading"
				><AiMarkdownInline :nodes="block.children"
			/></component>

			<ul
				v-else-if="block.type === 'list' && !block.ordered"
				:key="`ul${index}`"
				class="ai_md_list"
			>
				<li v-for="(item, itemIndex) in block.items" :key="itemIndex"
					><AiMarkdownInline :nodes="item"
				/></li>
			</ul>

			<ol
				v-else-if="block.type === 'list'"
				:key="`ol${index}`"
				class="ai_md_list"
			>
				<li v-for="(item, itemIndex) in block.items" :key="itemIndex"
					><AiMarkdownInline :nodes="item"
				/></li>
			</ol>

			<!-- Wrapper con scroll propio: una tabla de 5 columnas no puede
			ensanchar el panel ni empujar el hilo de mensajes. -->
			<div v-else-if="block.type === 'table'" :key="`tb${index}`" class="ai_md_table">
				<table>
					<thead>
						<tr>
							<th
								v-for="(cell, cellIndex) in block.head"
								:key="cellIndex"
								:class="alignClass(block.align[cellIndex])"
								><AiMarkdownInline :nodes="cell"
							/></th>
						</tr>
					</thead>
					<tbody>
						<tr v-for="(row, rowIndex) in block.rows" :key="rowIndex">
							<td
								v-for="(cell, cellIndex) in row"
								:key="cellIndex"
								:class="alignClass(block.align[cellIndex])"
								><AiMarkdownInline :nodes="cell"
							/></td>
						</tr>
					</tbody>
				</table>
			</div>

			<pre
				v-else-if="block.type === 'code'"
				:key="`co${index}`"
				class="ai_md_pre"
			><code>{{ block.value }}</code></pre>

			<blockquote
				v-else-if="block.type === 'quote'"
				:key="`q${index}`"
				class="ai_md_quote"
				><AiMarkdownInline :nodes="block.children"
			/></blockquote>

			<hr v-else :key="`hr${index}`" class="ai_md_hr" />
		</template>
	</div>
</template>

<script>
// Renderiza una respuesta de Phi (markdown) como árbol de componentes Vue —
// fase 12 del plan docs/PLAN_PHI_NIVEL3.md.
//
// Recibe el TEXTO crudo (no el AST) a propósito: el parseo vive en un computed,
// así Vue lo cachea por burbuja y un stream de N deltas re-parsea sólo la
// burbuja que cambió, no el hilo entero.
//
// Sin `v-html` ni sanitización de HTML (invariante 11): lo que el modelo manda
// viaja como texto en nodos reales.
import AiMarkdownInline from "@/components/aiChat/AiMarkdownInline.vue";
import { parseMarkdown } from "@/utils/markdown";

const ALIGN_CLASSES = {
	right: "is-right",
	center: "is-center",
	left: "",
};

export default {
	name: "AiMarkdown",
	components: { AiMarkdownInline },
	props: {
		text: {
			type: String,
			default: "",
		},
	},
	computed: {
		nodes() {
			return parseMarkdown(this.text);
		},
	},
	methods: {
		headingTag(level) {
			// h1/h2 del modelo se degradan: dentro de una burbuja de chat el
			// título más grande razonable es un h3.
			return `h${Math.max(3, Math.min(level + 2, 5))}`;
		},
		alignClass(alignment) {
			return ALIGN_CLASSES[alignment] || "";
		},
	},
};
</script>

<style lang="scss" scoped>
.ai_md {
	display: flex;
	flex-direction: column;
	gap: 8px;
	min-width: 0;
}

.ai_md_p {
	margin: 0;
	// Los saltos simples que el modelo pone dentro de un párrafo se leen como
	// saltos (es un chat, no un documento).
	white-space: pre-line;
	word-break: break-word;
}

.ai_md_heading {
	margin: 2px 0 0;
	color: var(--ai-chat-ink);
	font-size: 13.5px;
	font-weight: 700;
	line-height: 1.35;
}

.ai_md_list {
	margin: 0;
	padding-left: 18px;

	li {
		margin: 2px 0;
		word-break: break-word;
	}
}

ol.ai_md_list {
	list-style: decimal;
}

ul.ai_md_list {
	list-style: disc;
}

.ai_md_table {
	max-width: 100%;
	overflow-x: auto;
	scrollbar-width: thin;

	table {
		border-collapse: collapse;
		font-size: 12px;
		// Sin ancho fijo: la tabla mide lo que necesita y el wrapper scrollea.
		white-space: nowrap;
	}

	th,
	td {
		padding: 5px 9px;
		border: 1px solid var(--ai-chat-table-border);
		text-align: left;
		// Los montos y % se leen en columna: cifras de ancho parejo.
		font-variant-numeric: tabular-nums;

		&.is-right {
			text-align: right;
		}

		&.is-center {
			text-align: center;
		}
	}

	th {
		background: var(--ai-chat-table-head-bg);
		font-weight: 600;
	}
}

.ai_md_pre {
	margin: 0;
	padding: 8px 10px;
	border-radius: 8px;
	background: var(--ai-chat-code-bg);
	color: var(--ai-chat-ink);
	font-family: "SFMono-Regular", Consolas, "Liberation Mono", Menlo, monospace;
	font-size: 11.5px;
	line-height: 1.5;
	overflow-x: auto;
	scrollbar-width: thin;
}

.ai_md_quote {
	margin: 0;
	padding: 2px 0 2px 10px;
	border-left: 3px solid var(--ai-chat-quote-border);
	color: var(--ai-chat-muted);
}

.ai_md_hr {
	margin: 2px 0;
	border: none;
	border-top: 1px solid var(--ai-chat-border);
}
</style>
